import type {
  shared_footer_props,
  footer_link_key,
  footer_section_key,
} from "./types";

const footer_structure: Array<{
  title_key: footer_section_key;
  links: Array<{ text_key: footer_link_key; href: string }>;
}> = [
  {
    title_key: "freelancer",
    links: [
      { text_key: "findProject", href: "/job-search" },
      { text_key: "signup", href: "/signup" },
    ],
  },
  {
    title_key: "recruiter",
    links: [
      { text_key: "findFreelancer", href: "/freelancers" },
      { text_key: "signup", href: "/signup" },
      { text_key: "publishProject", href: "/jobs" },
    ],
  },
  {
    title_key: "community",
    links: [
      { text_key: "blog", href: "/ressources" },
      { text_key: "aboutPigeons", href: "/about" },
      { text_key: "terms", href: "/conditions#terms" },
      { text_key: "privacy", href: "/conditions#privacy" },
    ],
  },
  {
    title_key: "help",
    links: [
      { text_key: "faq", href: "/faq" },
      { text_key: "contactUs", href: "/contact" },
    ],
  },
];

export function SharedFooter({
  labels,
  app_base_url,
  home_href,
  logo_src,
  logo_alt,
  logo_width,
  logo_height,
  year,
  socials,
  locale_switcher,
  issue_report,
  render_internal_link,
}: shared_footer_props) {
  return (
    <footer className="mx-auto w-[80%] border-t border-secondary/10 pb-12 pt-8 font-ambit">
      <div className="mt-12 flex flex-col justify-between gap-12 lg:flex-row">
        <div className="grid flex-grow grid-cols-2 gap-8 lg:grid-cols-4">
          {footer_structure.map((column) => (
            <div key={column.title_key}>
              <h3 className="mb-6 text-[20px] font-bold text-secondary">
                {labels[column.title_key]}
              </h3>
              <ul className="space-y-4">
                {column.links.map((link) => {
                  const is_app_signup =
                    link.text_key === "signup" &&
                    (column.title_key === "freelancer" ||
                      column.title_key === "recruiter");
                  const is_app_publish_jobs =
                    link.text_key === "publishProject" &&
                    column.title_key === "recruiter";
                  const href = is_app_signup
                    ? `${app_base_url}/signup`
                    : is_app_publish_jobs
                      ? `${app_base_url}/jobs`
                      : link.href;
                  const is_external =
                    href.startsWith("mailto:") || href.startsWith("http");

                  if (is_external || is_app_signup || is_app_publish_jobs) {
                    return (
                      <li key={link.href + link.text_key}>
                        <a
                          href={href}
                          className="inline-block text-[17px] text-secondary/70 transition-colors hover:text-primary"
                        >
                          {labels[link.text_key]}
                        </a>
                      </li>
                    );
                  }

                  const hash_index = href.indexOf("#");
                  const pathname =
                    hash_index >= 0 ? href.slice(0, hash_index) : href;
                  const hash =
                    hash_index >= 0 ? href.slice(hash_index + 1) : undefined;

                  return (
                    <li key={link.href + link.text_key}>
                      {render_internal_link({
                        href: pathname,
                        hash,
                        class_name:
                          "inline-block text-[17px] text-secondary/70 transition-colors hover:text-primary",
                        children: labels[link.text_key],
                      })}
                    </li>
                  );
                })}
                {column.title_key === "help" ? (
                  <li className="text-[17px] text-secondary/70 [&_a]:inline-block [&_a]:transition-colors [&_a:hover]:text-primary [&_button]:inline-block [&_button]:text-left [&_button]:transition-colors [&_button:hover]:text-primary">
                    {issue_report}
                  </li>
                ) : null}
              </ul>
            </div>
          ))}
        </div>

        <div className="w-full lg:w-[380px]">
          <h3 className="mb-6 text-[20px] font-bold text-secondary">
            {labels.newsletterTitle}
          </h3>
          <div className="gradient-border-focus relative z-0 rounded-full group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary/40">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
            </div>
            <input
              type="email"
              aria-label={labels.emailAria}
              placeholder={labels.emailPlaceholder}
              className="relative z-10 h-[60px] w-full rounded-full border border-secondary/15 bg-white pl-14 pr-4 text-[17px] outline-none transition-all placeholder:text-secondary/30"
            />
          </div>
        </div>
      </div>

      <div className="mt-[60px] flex flex-col items-center justify-between gap-8 md:flex-row md:items-end">
        {render_internal_link({
          href: home_href,
          class_name: "transition-opacity hover:opacity-80",
          children: (
            <img
              src={logo_src}
              alt={logo_alt}
              width={logo_width}
              height={logo_height}
              style={{ width: `${logo_width}px`, height: `${logo_height}px` }}
              className="object-contain"
            />
          ),
        })}

        <div className="flex h-full flex-col items-center justify-end gap-8 md:items-end">
          <div className="flex items-center gap-4">
            {socials.map((social) => (
              <a
                key={social.id}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-110"
              >
                {social.icon_node ?? (
                  <img
                    src={social.icon_src}
                    alt={social.id}
                    width={28}
                    height={28}
                    className="h-7 w-7"
                  />
                )}
              </a>
            ))}
          </div>

          <div className="flex flex-col items-center gap-4 md:items-end">
            {locale_switcher}

            <div className="text-[15px] font-medium text-secondary/50">
              <span>{labels.copyright.replace("{year}", year)}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
